import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { faBinoculars } from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import Icon from '../../components/icon/Icon'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import {
  createQuickPassSavedSearch,
  deleteQuickPassSavedSearch,
  getQuickPassSavedSearchesForUser,
  runQuickPassSavedSearch,
} from '../../_data/quickpass-saved-searches'
import { QuickPassSavedSearch } from '../../_types/quickpass-saved-search'
import {
  FREQUENCY_OPTIONS,
  buildDefaultFormState,
  buildFormStateFromProfile,
  finaliseFormState,
  formatLastRun,
  summariseCriteria,
} from './helpers'
import { SavedSearchFormState, SavedSearchRunSummary } from './types'
import {
  PageStack,
  Header,
  HeaderRow,
  HeaderText,
  PageTitle,
  PageSubtitle,
  HeaderActions,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  EmptyCard,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
  SearchList,
  SearchCard,
  SearchCardTop,
  SearchCardHeading,
  SearchName,
  CriteriaLine,
  SearchCardMeta,
  Badge,
  SearchCardActions,
  RunResultBanner,
  FormCard,
  FormHeading,
  FormGrid,
  Field,
  FieldLabel,
  FieldInput,
  FieldSelect,
  NotifyToggleRow,
  FormActions,
  ErrorBanner,
} from './styled'

/**
 * Saved-search management page. Renter sees their existing searches,
 * can spin a new one from their onboarding preferences in one click,
 * compose a custom one inline, and trigger an on-demand run against
 * their imported listings. New matches trigger preference-match
 * notifications via the existing passive-signals pipeline.
 *
 * Cron-driven runs + email digests + web push are out of scope for this
 * slice — once they ship, the in-app history surfaced here will become
 * the source of truth for what fired when.
 *
 * @returns {JSX.Element}
 */
const SavedSearchesPage = (): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { profile } = useQuickPassProfile()

  const quickPassUserId = quickPassUser?.id ?? ''

  const [savedSearches, setSavedSearches] = useState<QuickPassSavedSearch[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [formState, setFormState] = useState<SavedSearchFormState | null>(null)
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false)
  const [runningSearchId, setRunningSearchId] = useState<string | null>(null)
  const [deletingSearchId, setDeletingSearchId] = useState<string | null>(null)
  const [lastRunSummary, setLastRunSummary] =
    useState<SavedSearchRunSummary | null>(null)

  useEffect(() => {
    if (quickPassUserId === '') {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)

    getQuickPassSavedSearchesForUser(quickPassUserId)
      .then(loaded => {
        if (cancelled === true) {
          return
        }
        setSavedSearches(loaded)
      })
      .catch((caughtError: unknown) => {
        if (cancelled === true) {
          return
        }
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Could not load your saved searches.'
        setErrorMessage(message)
      })
      .finally(() => {
        if (cancelled === true) {
          return
        }
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [quickPassUserId])

  const handleOpenCustomForm = useCallback((): void => {
    setFormState(buildDefaultFormState())
    setErrorMessage('')
  }, [])

  const handleOpenFromPreferencesForm = useCallback((): void => {
    setFormState(buildFormStateFromProfile(profile))
    setErrorMessage('')
  }, [profile])

  const handleCancelForm = useCallback((): void => {
    setFormState(null)
  }, [])

  const handleFormFieldChange = useCallback(
    <FieldName extends keyof SavedSearchFormState>(
      fieldName: FieldName,
      nextValue: SavedSearchFormState[FieldName],
    ): void => {
      setFormState(previous => {
        if (previous === null) {
          return previous
        }
        return { ...previous, [fieldName]: nextValue }
      })
    },
    [],
  )

  const handleSubmitForm = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()

      if (formState === null) {
        return
      }

      const finalised = finaliseFormState(formState)

      if (finalised === null) {
        setErrorMessage('Give your search a name first.')
        return
      }

      setIsSubmittingForm(true)
      setErrorMessage('')

      try {
        const created = await createQuickPassSavedSearch({
          quickPassUserId,
          name: finalised.name,
          criteria: finalised.criteria,
          frequency: finalised.frequency,
          notifyOnMatch: finalised.notifyOnMatch,
        })
        setSavedSearches(previous => [created, ...previous])
        setFormState(null)
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Saving failed. Try again in a sec.'
        setErrorMessage(message)
      } finally {
        setIsSubmittingForm(false)
      }
    },
    [formState, quickPassUserId],
  )

  const handleRunSearch = useCallback(
    async (savedSearch: QuickPassSavedSearch): Promise<void> => {
      setRunningSearchId(savedSearch.id)
      setErrorMessage('')

      try {
        const runResult = await runQuickPassSavedSearch(
          savedSearch.id,
          quickPassUserId,
        )

        setSavedSearches(previous =>
          previous.map(entry =>
            entry.id === savedSearch.id ? runResult.savedSearch : entry,
          ),
        )

        setLastRunSummary({
          savedSearchId: savedSearch.id,
          totalMatchCount: runResult.totalMatchListingIds.length,
          newMatchCount: runResult.newMatchListingIds.length,
          notificationsWritten: runResult.notificationsWritten,
          ranAt: Date.now(),
        })
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Could not run that search just now.'
        setErrorMessage(message)
      } finally {
        setRunningSearchId(null)
      }
    },
    [quickPassUserId],
  )

  const handleDeleteSearch = useCallback(
    async (savedSearch: QuickPassSavedSearch): Promise<void> => {
      const confirmation = window.confirm(
        `Delete "${savedSearch.name}"? This cannot be undone.`,
      )
      if (confirmation === false) {
        return
      }

      setDeletingSearchId(savedSearch.id)
      setErrorMessage('')

      try {
        await deleteQuickPassSavedSearch(savedSearch.id, quickPassUserId)
        setSavedSearches(previous =>
          previous.filter(entry => entry.id !== savedSearch.id),
        )
        if (lastRunSummary?.savedSearchId === savedSearch.id) {
          setLastRunSummary(null)
        }
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Delete failed. Try again in a sec.'
        setErrorMessage(message)
      } finally {
        setDeletingSearchId(null)
      }
    },
    [lastRunSummary, quickPassUserId],
  )

  const sortedSavedSearches = useMemo(() => {
    return [...savedSearches].sort(
      (left, right) => right.createdAt - left.createdAt,
    )
  }, [savedSearches])

  return (
    <AppLayout>
      <PageStack>
        <Header>
          <HeaderRow>
            <HeaderText>
              <PageTitle>Saved searches</PageTitle>
              <PageSubtitle>
                Set the criteria once and we&apos;ll watch your imported
                listings for matches. New hits land in your notifications.
              </PageSubtitle>
            </HeaderText>
            <HeaderActions>
              <SecondaryButton
                type="button"
                onClick={handleOpenFromPreferencesForm}
                disabled={profile === null || formState !== null}
              >
                Use my preferences
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={handleOpenCustomForm}
                disabled={formState !== null}
              >
                New custom search
              </PrimaryButton>
            </HeaderActions>
          </HeaderRow>
        </Header>

        {errorMessage !== '' && formState === null ? (
          <ErrorBanner role="alert">{errorMessage}</ErrorBanner>
        ) : null}

        {formState !== null ? (
          <FormCard>
            <FormHeading>New saved search</FormHeading>
            <form onSubmit={handleSubmitForm}>
              <FormGrid>
                <Field>
                  <FieldLabel htmlFor="saved-search-name">Name</FieldLabel>
                  <FieldInput
                    id="saved-search-name"
                    type="text"
                    value={formState.name}
                    placeholder="Toronto 2BR hunt"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleFormFieldChange('name', event.target.value)
                    }
                    required
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-cities">
                    Cities (comma-separated)
                  </FieldLabel>
                  <FieldInput
                    id="saved-search-cities"
                    type="text"
                    value={formState.citiesText}
                    placeholder="Toronto, Mississauga"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleFormFieldChange('citiesText', event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-price-min">
                    Price min ($/mo)
                  </FieldLabel>
                  <FieldInput
                    id="saved-search-price-min"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={formState.priceMinText}
                    placeholder="1500"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleFormFieldChange('priceMinText', event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-price-max">
                    Price max ($/mo)
                  </FieldLabel>
                  <FieldInput
                    id="saved-search-price-max"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={formState.priceMaxText}
                    placeholder="2800"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleFormFieldChange('priceMaxText', event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-bedrooms">
                    Bedrooms (comma-separated)
                  </FieldLabel>
                  <FieldInput
                    id="saved-search-bedrooms"
                    type="text"
                    value={formState.bedroomsText}
                    placeholder="1, 2"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleFormFieldChange('bedroomsText', event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-bathrooms">
                    Bathrooms (comma-separated)
                  </FieldLabel>
                  <FieldInput
                    id="saved-search-bathrooms"
                    type="text"
                    value={formState.bathroomsText}
                    placeholder="1, 1.5"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleFormFieldChange('bathroomsText', event.target.value)
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-frequency">
                    How often should we look?
                  </FieldLabel>
                  <FieldSelect
                    id="saved-search-frequency"
                    value={formState.frequency}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                      handleFormFieldChange(
                        'frequency',
                        event.target.value as SavedSearchFormState['frequency'],
                      )
                    }
                  >
                    {FREQUENCY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FieldSelect>
                </Field>
                <Field>
                  <FieldLabel htmlFor="saved-search-notify">
                    Notifications
                  </FieldLabel>
                  <NotifyToggleRow htmlFor="saved-search-notify">
                    <input
                      id="saved-search-notify"
                      type="checkbox"
                      checked={formState.notifyOnMatch}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        handleFormFieldChange(
                          'notifyOnMatch',
                          event.target.checked,
                        )
                      }
                    />
                    Notify me when new matches show up
                  </NotifyToggleRow>
                </Field>
              </FormGrid>

              {errorMessage !== '' ? (
                <ErrorBanner role="alert">{errorMessage}</ErrorBanner>
              ) : null}

              <FormActions>
                <SecondaryButton
                  type="button"
                  onClick={handleCancelForm}
                  disabled={isSubmittingForm}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={isSubmittingForm}>
                  {isSubmittingForm === true ? 'Saving…' : 'Save search'}
                </PrimaryButton>
              </FormActions>
            </form>
          </FormCard>
        ) : null}

        {isLoading === true ? (
          <CriteriaLine>Loading your saved searches…</CriteriaLine>
        ) : null}

        {isLoading === false && sortedSavedSearches.length === 0 ? (
          <EmptyCard>
            <EmptyEmoji aria-hidden="true">
              <Icon icon={faBinoculars} />
            </EmptyEmoji>
            <EmptyTitle>No saved searches yet</EmptyTitle>
            <EmptyBody>
              Save your hunt criteria once and QuickPass will scan your imported
              listings for matches whenever you ask — and soon, automatically on
              a schedule.
            </EmptyBody>
          </EmptyCard>
        ) : null}

        {sortedSavedSearches.length > 0 ? (
          <SearchList>
            {sortedSavedSearches.map(savedSearch => {
              const isRunning = runningSearchId === savedSearch.id
              const isDeleting = deletingSearchId === savedSearch.id
              const isBusy = isRunning || isDeleting
              const summaryForThis =
                lastRunSummary?.savedSearchId === savedSearch.id
                  ? lastRunSummary
                  : null

              return (
                <SearchCard key={savedSearch.id}>
                  <SearchCardTop>
                    <SearchCardHeading>
                      <SearchName>{savedSearch.name}</SearchName>
                      <CriteriaLine>
                        {summariseCriteria(savedSearch)}
                      </CriteriaLine>
                    </SearchCardHeading>
                    <SearchCardActions>
                      <SecondaryButton
                        type="button"
                        onClick={() => handleRunSearch(savedSearch)}
                        disabled={isBusy}
                      >
                        {isRunning === true ? 'Running…' : 'Run now'}
                      </SecondaryButton>
                      <DangerButton
                        type="button"
                        onClick={() => handleDeleteSearch(savedSearch)}
                        disabled={isBusy}
                      >
                        {isDeleting === true ? 'Deleting…' : 'Delete'}
                      </DangerButton>
                    </SearchCardActions>
                  </SearchCardTop>
                  <SearchCardMeta>
                    <Badge>{savedSearch.frequency}</Badge>
                    {savedSearch.notifyOnMatch === true ? (
                      <Badge>Notify on match</Badge>
                    ) : null}
                    <span>{formatLastRun(savedSearch.lastRunAt)}</span>
                    <span>
                      {savedSearch.lastMatchListingIds.length} current match
                      {savedSearch.lastMatchListingIds.length === 1 ? '' : 'es'}
                    </span>
                  </SearchCardMeta>
                  {summaryForThis !== null ? (
                    <RunResultBanner>
                      Found <strong>{summaryForThis.totalMatchCount}</strong>{' '}
                      total match
                      {summaryForThis.totalMatchCount === 1 ? '' : 'es'},
                      including <strong>{summaryForThis.newMatchCount}</strong>{' '}
                      new since last run.
                      {summaryForThis.notificationsWritten > 0
                        ? ` Wrote ${summaryForThis.notificationsWritten} notification${summaryForThis.notificationsWritten === 1 ? '' : 's'}.`
                        : ''}
                    </RunResultBanner>
                  ) : null}
                </SearchCard>
              )
            })}
          </SearchList>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default SavedSearchesPage
